import { FormEvent, useCallback, useEffect, useState } from 'react';
import { UsernamePanel } from '../usernamePanel/UsernamePanel';
import { AuthCodePanel } from '../authCodePanel/AuthCodePanel';
import { animated, useSpring } from 'react-spring';
import { PasswordPanel } from "../passwordPanel/PasswordPanel";
import { Panel } from '../loginPage/LoginPage';

require('./LoginForm.css');

export type Props = {
    busy: boolean;
    errorMessage: string;
    login(username: string, password: string, authCode?: string): void;
    goBack(): void;
    activePanel: Panel;
};

export const LoginForm = ({ busy, errorMessage, login, goBack, activePanel }: Props) => {
    let panelHostTransformPercentage: number = 0;
    
    if (activePanel === Panel.AuthCode) {
        panelHostTransformPercentage = -66.7;
    }
    else if (activePanel === Panel.Password) {
        panelHostTransformPercentage = -33.3;
    }
    else {
        panelHostTransformPercentage = 0;
    }
    const [ panelHostStyle, panelHostStyleAnim ] = useSpring(() => ({
        config: { mass: 1, tension: 400, friction: 30 },
        transform: `translate(${panelHostTransformPercentage}%,0px)`,
    }));

    const [ username, setUsername ] = useState('');
    const [ password, setPassword ] = useState('');
    const [ authCode, setAuthCode ] = useState('');

    const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        login(username, password, authCode);
    }, [ login, username, password, authCode ]);

    useEffect(() => {
        panelHostStyleAnim.start({
            transform: `translate(${panelHostTransformPercentage}%,0px)`,
        });
    }, [ panelHostTransformPercentage, panelHostStyleAnim ]);

    // Wipe out the auth code if the user clicks back
    const handleClickBack = useCallback(() => {
        setAuthCode('');
        setPassword('');
        goBack();
    }, [ setAuthCode, goBack ]);

    // Reset the password and auth code if the error message changes
    useEffect(() => {
        if (errorMessage) {
            setPassword('');
            setAuthCode('');
        }
    }, [ errorMessage ]);

    // Make sure animation exits cleanly when we get unmounted
    useEffect(() => {
        return () => {
            panelHostStyleAnim.stop();
        };
    }, [ panelHostStyleAnim ]);

    return (
        <form
            id="login-form"
            onSubmit={ handleSubmit }
            className={ `ui grid form${errorMessage ? ' error' : ''}${busy ? ' loading' : ''}` }
        >
            <animated.div
                className="panel-host"
                style={ panelHostStyle }
            >
                <UsernamePanel
                    active={ activePanel === Panel.Username }
                    busy={ busy }
                    username={ username }
                    setUsername={ setUsername }
                    errorMessage={ errorMessage }
                />

                <PasswordPanel
                    active={ activePanel === Panel.Password }
                    busy={ busy }
                    password={ password }
                    setPassword={ setPassword }
                    errorMessage={ errorMessage }
                />

                <AuthCodePanel
                    active={ activePanel === Panel.AuthCode }
                    busy={ busy }
                    authCode={ authCode }
                    setAuthCode={ setAuthCode }
                    errorMessage={ errorMessage }
                    onGoBack={ handleClickBack }
                />
            </animated.div>
        </form>
    );
};
